import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Button,
  alpha,
  Chip,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux'; // LAP-733 (GE022) round 10 fix-up
import { useSubscription } from './SubscriptionContext';
import LockIcon from '@mui/icons-material/Lock';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { getButtonTarget, goToButtonTarget, shouldShowUpgradePrompt } from './publicationHandlers';
import { getTranslation, getUserLanguage } from './translations'; // LAP-733 (GE022) round 10 fix-up
import DiscoveryCardActions from './DiscoveryCardActions'; // GE023 — actions rapides cartes découverte

// LAP-733 (GE022) round 10 fix-up — map server-side publication.type to i18n key
const PUBLICATION_TYPE_KEY = {
  Insight: 'publication',
  Publication: 'publication',
  Summary: 'summary',
  Reminder: 'reminder',
  Actionable: 'actionable',
  News: 'news',
};

const ActionableCard = ({ publication, onInteraction, onUpgradeRequest, showArchivedIndicators = false }) => {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const [isLocked, setIsLocked] = useState(false);

  // LAP-733 (GE022) round 10 fix-up — resolve user language + BCP-47 locale
  const siteUser = useSelector((store) => store.user);
  const lang = getUserLanguage(siteUser);
  const dateLocale = ({ fr: 'fr-FR', en: 'en-GB', de: 'de-DE', it: 'it-IT', es: 'es-ES' })[lang] || lang || 'fr-FR';

  // LAP-733 (GE022) round 10 fix-up — translate the publication.type badge
  const displayType = getTranslation(PUBLICATION_TYPE_KEY[publication.type] || 'publication', lang);

  useEffect(() => {
    const shouldLock = shouldShowUpgradePrompt(publication, subscription);
    setIsLocked(shouldLock);
  }, [publication, subscription]);

  const handleAction = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLocked) {
      if (onUpgradeRequest) {
        onUpgradeRequest();
      }
      return;
    }
    let target = publication.button_target;
    if (!target) {
      const definePublicationId = publication.define_publication?.id || publication.define_publication_id;
      if (definePublicationId) {
        target = getButtonTarget(definePublicationId, publication.domain, publication.type);
      }
    }
    if (target) {
      goToButtonTarget(target, navigate, publication);
      onInteraction(publication.id, 'click');
    } else {
      console.warn('No route found for publication:', publication.id, publication.domain, publication.type);
    }
  };

  const handleLike = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'like');
  };

  const handleSave = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'save');
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    onInteraction(publication.id, 'dismiss');
  };

  const definePublicationId = publication.define_publication?.id || publication.define_publication_id;
  const resolvedTarget = publication.button_target || (definePublicationId ? getButtonTarget(definePublicationId, publication.domain, publication.type) : null);
  const hasActionButton = (publication.button_text || publication.button_target) && !!resolvedTarget;

  const actionableColor = '#f59e0b'; // Orange
  const actionableGradient = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
  const isCompleted = publication.status === 'completed' || 
                      publication.status === 'completed_active' || 
                      publication.status === 'completed_archived';

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1a1d2e',
        borderRadius: '16px',
        border: `1px solid ${isCompleted ? alpha('#10b981', 0.5) : alpha(actionableColor, 0.3)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        opacity: isLocked ? 0.7 : (isCompleted ? 0.7 : 1),
        '&:hover': {
          transform: isCompleted ? 'none' : 'translateY(-4px)',
          boxShadow: isCompleted ? 'none' : `0 20px 25px -5px ${alpha(actionableColor, 0.4)}, 0 10px 10px -5px ${alpha(
            actionableColor,
            0.3,
          )}`,
          borderColor: isCompleted ? alpha('#10b981', 0.7) : alpha(actionableColor, 0.5),
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: isCompleted ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : actionableGradient,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="start" gap={1} flexGrow={1}>
            <Typography
              variant="h4"
              component="span"
              sx={{
                fontSize: '2rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              }}
            >
              {publication.icon || '🎯'}
            </Typography>

            <Box flexGrow={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    lineHeight: 1.4,
                    color: '#E0E0E0',
                    mb: 0.5,
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    opacity: isCompleted ? 0.6 : 1,
                  }}
                >
                  {publication.title}
                </Typography>
                {isCompleted && (
                  <TaskAltIcon sx={{ color: '#10b981', fontSize: '1.2rem', mb: 0.5 }} />
                )}
              </Box>

              {publication.subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#E0E0E0', 0.7),
                    fontSize: '0.85rem',
                    mb: 1.5,
                  }}
                >
                  {publication.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          
          <Box display="flex" gap={0.5}>
            {isLocked && (
              <Chip
                icon={<LockIcon />}
                label="Premium"
                size="small"
                sx={{
                  bgcolor: alpha('#f59e0b', 0.2),
                  color: '#f59e0b',
                  fontSize: '0.7rem',
                  height: '24px',
                }}
              />
            )}
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  color: '#ef4444',
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Image */}
        {publication.image_url && (
          <Box
            sx={{
              width: '100%',
              borderRadius: '12px',
              overflow: 'hidden',
              mb: 2,
              mt: 1,
            }}
          >
            <img
              src={publication.image_url}
              alt={publication.title}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'cover',
                maxHeight: '300px',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}

        {/* Text Content */}
        {publication.text1 && (
          <Typography
            variant="body2"
            sx={{
              color: alpha('#E0E0E0', 0.9),
              fontSize: '0.9rem',
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            {publication.text1}
          </Typography>
        )}

        {/* GE023 — raison de recommandation (text2 = « Recommandé car … »,
            snapshotté par le publisher Discovery ; jamais généré côté front) */}
        {publication.text2 && (
          <Typography
            variant="body2"
            sx={{
              color: alpha('#E0E0E0', 0.6),
              fontSize: '0.8rem',
              fontStyle: 'italic',
              lineHeight: 1.5,
              mb: 2,
            }}
          >
            {publication.text2}
          </Typography>
        )}

        {/* Action button only when there is a valid target to navigate to */}
        {!isCompleted && hasActionButton && (
          <Button
            variant="contained"
            fullWidth
            onClick={handleAction}
            disabled={isLocked}
            sx={{
              mt: 'auto',
              py: 1.5,
              bgcolor: actionableColor,
              color: 'white',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              borderRadius: '12px',
              '&:hover': {
                bgcolor: '#d97706',
                transform: 'scale(1.02)',
              },
              '&:disabled': {
                bgcolor: alpha(actionableColor, 0.3),
                color: alpha('#fff', 0.5),
              },
            }}
            startIcon={isLocked ? <LockIcon /> : null}
          >
            {isLocked ? 'Upgrade to unlock' : (publication.button_text || 'Voir')}
          </Button>
        )}
        
        {/* Completed Badge */}
        {isCompleted && (
          <Box 
            display="flex" 
            alignItems="center" 
            gap={1} 
            mt="auto"
            mb={2}
            sx={{
              bgcolor: alpha('#10b981', 0.15),
              border: `1px solid ${alpha('#10b981', 0.3)}`,
              borderRadius: '8px',
              p: 1.5,
            }}
          >
            <TaskAltIcon sx={{ color: '#10b981', fontSize: '1.5rem' }} />
            <Typography
              variant="body2"
              sx={{
                color: '#10b981',
                fontWeight: 600,
                fontSize: '0.9rem',
              }}
            >
              Completed
            </Typography>
          </Box>
        )}

        {/* GE023 — actions rapides « Intéressé / Pas intéressé » (cartes
            découverte uniquement : rend null pour les autres cartes).
            Pas intéressé = apprentissage backend + dismiss de la carte. */}
        <DiscoveryCardActions
          publication={publication}
          onRemove={() => onInteraction(publication.id, 'dismiss')}
        />

        {/* Interaction buttons */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              onClick={handleLike}
              sx={{
                color: publication.was_liked ? '#ef4444' : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              {publication.was_liked ? (
                <FavoriteIcon fontSize="small" />
              ) : (
                <FavoriteBorderIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleSave}
              sx={{
                color: publication.is_saved ? '#f59e0b' : 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                },
              }}
            >
              {publication.is_saved ? (
                <BookmarkIcon fontSize="small" />
              ) : (
                <BookmarkBorderIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            {/* Type Chip */}
            <Chip
              label={displayType}
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '20px',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
            {/* Show saved/liked indicators in archived view */}
            {showArchivedIndicators && (
              <>
                {publication.is_saved && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: alpha('#8b5cf6', 0.2),
                      color: '#8b5cf6',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                    }}
                  >
                    <BookmarkIcon sx={{ fontSize: '0.7rem' }} />
                    Saved
                  </Box>
                )}
                {publication.was_liked && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: alpha('#ef4444', 0.2),
                      color: '#ef4444',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                    }}
                  >
                    <FavoriteIcon sx={{ fontSize: '0.7rem' }} />
                    Liked
                  </Box>
                )}
              </>
            )}
            {publication.datetime_displayed && (
              <Typography
                variant="caption"
                sx={{
                  color: alpha('#E0E0E0', 0.5),
                  fontSize: '0.75rem',
                }}
              >
                {new Date(publication.datetime_displayed).toLocaleDateString(dateLocale) /* LAP-733 (GE022) round 10 fix-up */}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ActionableCard;

